import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios, { AxiosError } from "axios";
// import { useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useState } from "react";
import { Eye, Loader } from "lucide-react";
import { EyeClosedIcon } from "@radix-ui/react-icons";

import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import {
  checkinEmployee,
  createEmployeeActivity,
  loginEmployee,
} from "@/config/mutation/employee";
import { verifyEmailOtp, verifyOtp } from "@/config/mutation/otp-less";
import { startSession } from "@/lib/session";

// Both OTP paths sign in for the same length of time. The e-mail path used to
// store a cookie with no expiry at all, which the browser drops when it closes.
const SESSION_HOURS = 15;

const formSchema = z.object({
  email: z
    .string({
      required_error: "Email is required!",
    })
    .email({
      message: "Invalid Email",
    }),
  password: z.string({
    required_error: "Password required!",
  }),
  phone: z.string().optional(),
  otp: z.string().optional(),
  emailOtp: z.string().optional(),
  orderId: z.string().optional(),
  token: z.string().optional(),
  id: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

function getOS() {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = [
    "Macintosh",
    "MacIntel",
    "MacPPC",
    "Mac68K",
    "Darwin",
    "darwin",
  ];
  const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];
  const iosPlatforms = ["iPhone", "iPad", "iPod"];
  let os = "Unknown OS";

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = "Mac OS";
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = "iOS";
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = "Windows";
  } else if (/Android/.test(userAgent)) {
    os = "Android";
  } else if (!os && /Linux/.test(platform)) {
    os = "Linux";
  }

  // console.log("OS", os);
  return os;
}

function getDevice() {
  let deviceType = "Personal Computer";

  if (isMobile) {
    deviceType = "Mobile";
  } else if (isTablet) {
    deviceType = "Tablet";
  }

  // console.log("device", deviceType);
  return deviceType;
}

function getBrowser() {
  const { userAgent } = window.navigator;

  // Determine the browser using user agent
  let browser: string = "";

  if (userAgent.indexOf("Chrome") > -1) {
    browser = "Google Chrome";
  } else if (userAgent.indexOf("Firefox") > -1) {
    browser = "Mozilla Firefox";
  } else if (userAgent.indexOf("Safari") > -1) {
    browser = "Apple Safari";
  } else if (userAgent.indexOf("Edge") > -1) {
    browser = "Microsoft Edge";
  } else if (
    userAgent.indexOf("MSIE") > -1 ||
    !!document["DOCUMENT_NODE"] === true
  ) {
    browser = "Internet Explorer";
  } else {
    browser = "Unknown";
  }

  // console.log("browser", browser);
  return browser;
}

function getScreenSize() {
  const width = window.innerWidth;
  const height = window.innerHeight;

  // console.log(`${width}px X ${height}px`);
  return `${width}px X ${height}px`;
}

const getIpAddress = async () => {
  try {
    const res = await axios.get("https://api.ipify.org/?format=json");
    return res?.data?.ip as string;
  } catch (error) {
    console.log("IP_FETCHING_ERROR", error);
    return "";
  }
};

export const EmployeeLoginForm = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      phone: "",
      otp: "",
      orderId: "",
      token: "",
      id: "",
      emailOtp: "",
    },
  });

  const phone = useWatch({
    control: form.control,
    name: "phone",
  });

  const otp = useWatch({
    control: form.control,
    name: "otp",
  });
  const emailOtp = useWatch({
    control: form.control,
    name: "emailOtp",
  });

  console.log({
    otp,
    emailOtp,
  });

  const [passwordView, setPasswordView] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: loginEmployee,
  });

  const { mutateAsync: VerifyOtp, isPending: isOtpVerifying } = useMutation({
    mutationFn: verifyOtp,
  });

  const { mutateAsync: VerifyEmailOTP, isPending: isEmailOTPVerifying } =
    useMutation({
      mutationFn: verifyEmailOtp,
    });

  const { mutate: CreateEmployeeActivity } = useMutation({
    mutationFn: createEmployeeActivity(),
  });

  const { mutateAsync } = useMutation({
    mutationFn: checkinEmployee,
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // Do something with the form values.
    // ✅ This will be type-safe and validated.
    // console.log(values);

    mutate(values, {
      onSuccess: async (data) => {
        form.setValue("phone", data?.employee?.phone);
        form.setValue("id", data?.employee?._id);
        form.setValue("orderId", data?.response?.orderId);
        form.setValue("token", data?.key);

        setStep(2);

        toast.success("Opt Sent. Valid for 3 minutes");
        // console.log("onSucess Login", data);
        // const responseData = data as {
        //   key: string;
        // };
        // setUser(data?.key, "employee");
        // setisAuth(true);
        // toast.success("Login Successful!");
        // navigate("/employee");
      },

      onError: (error) => {
        console.log("ERROR WHILE LOGGIN IN AS EMPLOYEE", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  }

  const handleVerifyOtp = async () => {
    if (!phone || phone.length !== 10)
      return toast.error("Phone number not found");

    if (!otp || otp.length !== 6) {
      return toast.error("Provide valid otp");
    }

    await VerifyOtp(
      {
        phone: phone,
        orderId: form.getValues("orderId") as string,
        otp: otp,
      },
      {
        onSuccess: async (data) => {
          const responseData = data?.data as {
            success: boolean;
            response: {
              isOTPVerified: boolean;
              reason?: string;
            };
          };

          const ip = await getIpAddress();

          if (responseData?.success && responseData?.response?.isOTPVerified) {
            await mutateAsync(
              {
                token: form.getValues("token") as string,
                // token: "",
                employee: form.getValues("id") as string,
                ip,
                device: getDevice(),
                browser: getBrowser(),
                os: getOS(),
                screenSize: getScreenSize(),
              },
              {
                onSuccess: async (data) => {
                  const comingData = data as {
                    lateCheckin: boolean;
                    lateCheckinsRemaining: number;
                    success: boolean;
                  };

                  if (comingData?.lateCheckin) {
                    if (comingData?.lateCheckinsRemaining > 0) {
                      toast(
                        <div className="flex flex-col">
                          <h3 className="text-lg font-semibold">
                            Login Successfull!
                          </h3>
                          <p>
                            You are late. If it happens{" "}
                            {comingData?.lateCheckinsRemaining} more time, then
                            your attendance will be marked as "Half Day"
                          </p>
                        </div>
                      );
                    } else {
                      toast(
                        <div className="flex flex-col">
                          <h3>Login Successfull!</h3>
                          <p>
                            You are late. Your attendance will be marked as
                            "Half Day"
                          </p>
                        </div>
                      );
                    }
                  } else {
                    toast.success("Login Successful!");
                  }

                  CreateEmployeeActivity({
                    employee: form.getValues("id") as string,
                    mode: "LOGIN",
                    ip,
                    device: getDevice(),
                    browser: getBrowser(),
                    os: getOS(),
                    screenSize: getScreenSize(),
                  });
                  startSession(form.getValues("token") as string, "employee", SESSION_HOURS);

                  setTimeout(() => {
                    navigate("/employee");
                  }, 300);
                },
                onError: (error) => {
                  console.log("ERROR WHILE LOGGIN IN AS EMPLOYEE", error);
                  const isAxiosError = error instanceof AxiosError;
                  if (isAxiosError) {
                    const errorReason = error?.response?.data?.error as string;
                    toast.error(errorReason);
                  } else {
                    toast.error(error?.message);
                  }
                },
              }
            );

            // setUser(form.getValues("token") as string, "employee");
            // setisAuth(true);
          } else if (
            responseData?.success &&
            !responseData?.response?.isOTPVerified
          ) {
            toast.error(
              responseData?.response?.reason || "Something wrong happend."
            );
          }
        },

        onError: (error) => {
          console.log("ERROR WHILE VERIFYING OTP", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(
              error?.response?.data?.response?.message || errorReason
            );
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const handleEmailOtp = async () => {
    await VerifyEmailOTP(
      {
        email: form.getValues("email"),
        otp: form.getValues("emailOtp") as string,
      },
      {
        onSuccess: async (data) => {
          const responseData = data?.data as {
            success: boolean;
            message: string;
            error: string;
          };

          const ip = await getIpAddress();

          if (responseData?.success) {
            await mutateAsync(
              {
                token: form.getValues("token") as string,
                employee: form.getValues("id") as string,
                ip,
                device: getDevice(),
                browser: getBrowser(),
                os: getOS(),
                screenSize: getScreenSize(),
              },
              {
                onSuccess: async (data) => {
                  const comingData = data as {
                    lateCheckin: boolean;
                    lateCheckinsRemaining: number;
                    success: boolean;
                  };

                  if (comingData?.lateCheckin) {
                    if (comingData?.lateCheckinsRemaining > 0) {
                      toast(
                        <div className="flex flex-col">
                          <h3 className="text-lg font-semibold">
                            Login Successfull!
                          </h3>
                          <p>
                            You are late. If it happens{" "}
                            {comingData?.lateCheckinsRemaining} more time, then
                            your attendance will be marked as "Half Day"
                          </p>
                        </div>
                      );
                    } else {
                      toast(
                        <div className="flex flex-col">
                          <h3>Login Successfull!</h3>
                          <p>
                            You are late. Your attendance will be marked as
                            "Half Day"
                          </p>
                        </div>
                      );
                    }
                  } else {
                    toast.success("Login Successful!");
                  }

                  CreateEmployeeActivity({
                    employee: form.getValues("id") as string,
                    mode: "LOGIN",
                    ip,
                    device: getDevice(),
                    browser: getBrowser(),
                    os: getOS(),
                    screenSize: getScreenSize(),
                  });
                  startSession(form.getValues("token") as string, "employee", SESSION_HOURS);

                  setTimeout(() => {
                    navigate("/employee");
                  }, 300);
                },
                onError: (error) => {
                  console.log("ERROR WHILE LOGGIN IN AS EMPLOYEE", error);
                  const isAxiosError = error instanceof AxiosError;
                  if (isAxiosError) {
                    const errorReason = error?.response?.data?.error as string;
                    toast.error(errorReason);
                  } else {
                    toast.error(error?.message);
                  }
                },
              }
            );
          } else {
            toast.error(responseData?.error || "Something wrong happend.");
          }
        },

        onError: (error) => {
          console.log("ERROR WHILE VERIFYING EMAIL OTP", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason || error?.message);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  };

  const handleFinalLogin = async () => {
    if (otp !== "") {
      await handleVerifyOtp();
    } else if (emailOtp !== "") {
      await handleEmailOtp();
    } else {
      return toast.error("Please enter one of the otp.");
    }
  };

  return (
    <div className="max-w-md w-full rounded-md">
      {step === 1 ? (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      {...field}
                      className="border-white/50"
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={passwordView ? "text" : "password"}
                        {...field}
                        className="border-white/50"
                        disabled={isPending}
                      />
                      <Button
                        type="button"
                        size="icon"
                        className="w-5 h-5 absolute right-2 top-2 z-[2] bg-[#f4ca8d] text-[#01020d] hover:bg-[#f4ca8d]/80"
                        onClick={() => {
                          setPasswordView(!passwordView);
                        }}
                      >
                        {passwordView ? (
                          <Eye className="w-3 h-3" />
                        ) : (
                          <EyeClosedIcon className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="bg-[#f4ca8d] text-[#01020d] hover:bg-[#f4ca8d]/80"
              disabled={isPending || isOtpVerifying}
            >
              Send OTP
            </Button>
          </form>
        </Form>
      ) : (
        <div className="flex flex-col gap-7">
          <div className="flex flex-col gap-7">
            <label htmlFor="Otp" className="flex flex-col">
              <span>WhatsApp OTP</span>
              <Input
                disabled={form.getValues("orderId") === ""}
                placeholder="Your Otp..."
                type="text"
                {...form.register("otp", { required: true })}
              />
            </label>

            <div className="w-full border-t border-t-gray-400 relative">
              <span className="absolute left-1/2 -translate-x-1/2 bottom-1/2 translate-y-1/2 bg-[#01020d] text-white p-1 rounded-full">
                OR
              </span>
            </div>

            <label htmlFor="Email_Otp" className="flex flex-col">
              <span>Email OTP</span>
              <Input
                placeholder="Your Email Otp..."
                type="text"
                {...form.register("emailOtp", { required: true })}
              />
            </label>
          </div>

          <div className="text-[13px]">
            <span>NOTE : </span>
            <span>You can login with your WhatsApp OTP or Email OTP</span>
          </div>

          <Button
            className="shrink-0 w-full bg-[#f4ca8d] text-[#01020d] hover:bg-[#f4ca8d]/80"
            type="button"
            disabled={
              (otp === "" && emailOtp === "") ||
              isOtpVerifying ||
              isEmailOTPVerifying
            }
            onClick={handleFinalLogin}
          >
            {isOtpVerifying || isEmailOTPVerifying ? (
              <Loader className="animate-spin" size={20} />
            ) : (
              "Login"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};
